// One deal: who to write to, the email thread, and the AI-drafted pitch
// you review, edit and approve before anything is sent.

import { useEffect, useState, type FormEvent } from 'react';
import {
  MESSAGE_STATUS,
  STAGE_LABELS,
  money,
  useContacts,
  useCreateContact,
  useDealMessages,
  useDiscardMessage,
  useEditMessage,
  useRunAgent,
  useSendMessage,
  useUpdateDeal,
  type Company,
  type Deal,
  type Message,
} from '../../lib/sponsors';
import { useJob } from '../../lib/transcripts';
import { jobError } from '../../lib/jobs';
import { errorInfo } from '../../lib/api';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { TextArea, TextField } from '../ui/Field';
import { Badge, Loading } from '../ui/Feedback';
import { useToast } from '../ui/Toast';

export function DealDialog({ deal, company, onClose }: { deal: Deal | null; company: Company | undefined; onClose: () => void }) {
  return (
    <Dialog
      open={!!deal}
      onClose={onClose}
      wide
      title={company?.name ?? 'Deal'}
      description={deal ? `${STAGE_LABELS[deal.stage]}${company?.domain ? ` · ${company.domain}` : ''}` : undefined}
      actions={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {deal && <DealBody deal={deal} company={company} />}
    </Dialog>
  );
}

function DealBody({ deal, company }: { deal: Deal; company: Company | undefined }) {
  const contacts = useContacts(deal.companyId);
  const hasEmail = (contacts.data ?? []).some((c) => c.email);
  return (
    <div className="space-y-5">
      {company?.description && <p className="text-sm text-muted">{company.description}</p>}
      <Value deal={deal} />
      <section aria-label="Contacts" className="space-y-2">
        <h3 className="text-sm font-semibold">Who to write to</h3>
        {contacts.isLoading && <Loading />}
        {(contacts.data ?? []).map((c) => (
          <div key={c.id} className="text-sm">
            {c.name}
            {c.role && <span className="text-muted"> · {c.role}</span>}
            {c.email ? <span className="text-muted"> · {c.email}</span> : <span className="text-warning"> · no email</span>}
          </div>
        ))}
        {contacts.data && !hasEmail && (
          <p className="text-xs text-warning">Add a contact with an email so outreach can be sent.</p>
        )}
        <AddContact companyId={deal.companyId} />
      </section>
      <Thread deal={deal} canSend={hasEmail} />
    </div>
  );
}

function Value({ deal }: { deal: Deal }) {
  const update = useUpdateDeal();
  const toast = useToast();
  const [value, setValue] = useState(deal.dealValueCents !== null ? String(deal.dealValueCents / 100) : '');
  const save = (e: FormEvent) => {
    e.preventDefault();
    const n = Number.parseFloat(value.replace(/[^\d.]/g, ''));
    if (!Number.isFinite(n)) return;
    update.mutate(
      { id: deal.id, dealValueCents: Math.round(n * 100) },
      {
        onSuccess: () => toast.show('Deal value saved', 'success'),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );
  };
  return (
    <form onSubmit={save} className="flex items-end gap-2">
      <TextField
        label="Deal value"
        hint={deal.dealValueCents !== null ? `Now ${money(deal.dealValueCents, deal.currency)}` : 'What this sponsorship is worth'}
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button type="submit" variant="secondary" loading={update.isPending} disabled={!value.trim()}>
        Save
      </Button>
    </form>
  );
}

function AddContact({ companyId }: { companyId: string }) {
  const create = useCreateContact();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  if (!open)
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        + Add contact
      </Button>
    );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { companyId, name: name.trim(), ...(email.trim() ? { email: email.trim() } : {}), ...(role.trim() ? { role: role.trim() } : {}) },
          {
            onSuccess: () => setOpen(false),
            onError: (err) => toast.show(errorInfo(err).message, 'error'),
          },
        );
      }}
      className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-3"
    >
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
      <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <TextField label="Role" placeholder="Partnerships" value={role} onChange={(e) => setRole(e.target.value)} maxLength={100} />
      <div className="flex gap-2 sm:col-span-3">
        <Button size="sm" type="submit" loading={create.isPending} disabled={!name.trim()}>
          Save contact
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Thread({ deal, canSend }: { deal: Deal; canSend: boolean }) {
  const agent = useRunAgent();
  const toast = useToast();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);
  const drafting = !!jobId && job.data?.status !== 'completed' && job.data?.status !== 'failed';
  const messages = useDealMessages(deal.id, drafting);

  useEffect(() => {
    if (job.data?.status === 'completed') {
      void messages.refetch();
      setJobId(null);
    }
  }, [job.data?.status, messages]);

  const draft = () =>
    agent.mutate(
      { type: 'generate-outreach', dealId: deal.id },
      {
        onSuccess: (r) => setJobId(r.jobId),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );

  return (
    <section aria-label="Emails" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Emails</h3>
        <Button size="sm" variant="secondary" onClick={draft} loading={agent.isPending || drafting}>
          {drafting ? 'Drafting…' : 'Draft a pitch with AI'}
        </Button>
      </div>
      {job.data?.status === 'failed' && (
        <p className="text-sm text-danger" title={job.data.errorMessage ?? undefined}>
          {jobError(job.data.errorMessage)}
        </p>
      )}
      {messages.isLoading && <Loading />}
      {messages.data?.length === 0 && !drafting && (
        <p className="text-sm text-muted">No emails yet. Let the AI draft a pitch from your show profile — you’ll review it first.</p>
      )}
      {(messages.data ?? []).map((m) => (
        <MessageView key={m.id} message={m} dealId={deal.id} canSend={canSend} />
      ))}
    </section>
  );
}

function MessageView({ message: m, dealId, canSend }: { message: Message; dealId: string; canSend: boolean }) {
  const editable = m.direction === 'outbound' && m.status !== 'sent';
  const [subject, setSubject] = useState(m.subject ?? '');
  const [body, setBody] = useState(m.body ?? '');
  const edit = useEditMessage(dealId);
  const discard = useDiscardMessage(dealId);
  const send = useSendMessage(dealId);
  const toast = useToast();
  const changed = subject !== (m.subject ?? '') || body !== (m.body ?? '');
  const onError = (err: unknown) => toast.show(errorInfo(err).message, 'error');

  return (
    <article className={`space-y-2 rounded-lg border p-3 ${m.direction === 'inbound' ? 'border-accent-soft bg-accent-soft/40' : 'border-border'}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>{m.direction === 'inbound' ? 'From the sponsor' : 'To the sponsor'}</span>
        <Badge tone={m.status === 'pending_approval' ? 'warning' : m.status === 'sent' ? 'success' : 'neutral'}>
          {MESSAGE_STATUS[m.status] ?? m.status}
        </Badge>
        {m.aiGenerated && <span>AI draft</span>}
        <span className="ml-auto">{new Date(m.sentAt ?? m.createdAt).toLocaleString()}</span>
      </div>
      {editable ? (
        <div className="space-y-2">
          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={500} />
          <TextArea label="Message" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!canSend || !body.trim()}
              loading={send.isPending}
              title={canSend ? undefined : 'Add a contact with an email first'}
              onClick={async () => {
                try {
                  // Send exactly what's on screen.
                  if (changed) await edit.mutateAsync({ id: m.id, subject, body });
                  await send.mutateAsync(m.id);
                  toast.show('Approved — sending now', 'success');
                } catch (err) {
                  onError(err);
                }
              }}
            >
              Approve & send
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!changed}
              loading={edit.isPending && !send.isPending}
              onClick={() => edit.mutate({ id: m.id, subject, body }, { onSuccess: () => toast.show('Draft saved', 'success'), onError })}
            >
              Save draft
            </Button>
            <Button size="sm" variant="ghost" loading={discard.isPending} onClick={() => discard.mutate(m.id, { onError })}>
              Discard
            </Button>
          </div>
        </div>
      ) : (
        <>
          {m.subject && <div className="text-sm font-medium">{m.subject}</div>}
          <p className="whitespace-pre-wrap text-sm">{m.body}</p>
        </>
      )}
    </article>
  );
}
